import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { leaveBalanceServices } from '../../services/leaveBalanceServices';
import { useSnackbar } from '../../utils/useSnackbar';
import TableSkeleton from '../common/TableSkeleton';
import EmptyState from '../common/EmptyState';
import PageHeader from '../ui/PageHeader';
import '../../css/Leave.css';
import '../../css/DataTable.css';

const CustomNoRowsOverlay = () => (
  <EmptyState
    icon={<EventBusyRoundedIcon sx={{ fontSize: 30 }} />}
    title="No Balance History Found"
    description="There are no leave credits, deductions, or manual adjustments recorded for this employee yet."
  />
);

const formatDateTime = (val) => {
  if (!val) return '—';
  return val;
};

const formatReasonLabel = (reason) => {
  if (!reason) return 'Adjustment';
  switch (reason) {
    case 'MONTHLY_CREDIT': return 'Monthly Credit';
    case 'MANUAL_ADJUST': return 'Manual Adjustment';
    case 'LEAVE_APPROVED': return 'Leave Approved (Deduction)';
    case 'LEAVE_CANCELLED': return 'Leave Cancelled (Credit)';
    default: return reason;
  }
};

const getReasonColor = (reason) => {
  switch (reason) {
    case 'MONTHLY_CREDIT': return 'info';
    case 'MANUAL_ADJUST': return 'warning';
    case 'LEAVE_APPROVED': return 'error';
    case 'LEAVE_CANCELLED': return 'success';
    default: return 'default';
  }
};

const LeaveBalanceAuditPage = () => {
  const { data: userData } = useSelector((state) => state.userDetails);
  const location = useLocation();
  const navigate = useNavigate();

  const targetUserId = location.state?.userId || userData?.id;
  const targetUserName = location.state?.userName || (userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : 'User');

  const [auditLogs, setAuditLogs] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([]);

  const pageSizeOptions = [5, 10, 15, 25];
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  // Fetch audit log
  const fetchAuditLog = useCallback(async () => {
    if (!targetUserId) return;
    try {
      setLoading(true);
      const sortField = sortModel?.[0]?.field || 'id';
      const sortDir = sortModel?.[0]?.sort || 'desc';
      const response = await leaveBalanceServices.getAuditLog(
        targetUserId,
        paginationModel.page,
        paginationModel.pageSize,
        sortField,
        sortDir
      );
      setAuditLogs(response?.content || []);
      setRowCount(response?.totalElements || 0);
    } catch (err) {
      console.error('Failed to fetch leave balance audit logs:', err);
      showSnackbar('Failed to load leave balance history.', 'error');
    } finally {
      setLoading(false);
    }
  }, [targetUserId, paginationModel.page, paginationModel.pageSize, sortModel, showSnackbar]);

  useEffect(() => {
    fetchAuditLog();
  }, [fetchAuditLog]);

  const handleSortModelChange = (newModel) => {
    setSortModel(newModel || []);
  };

  const columns = useMemo(() => [
    {
      field: 'createdAt',
      headerName: 'Date & Time',
      flex: 1.8,
      minWidth: 170,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>
            {formatDateTime(params.value)}
          </span>
        </div>
      ),
    },
    {
      field: 'amount',
      headerName: 'Change',
      flex: 1.2,
      minWidth: 120,
      renderCell: (params) => {
        const amt = params.value;
        const isPositive = amt > 0;
        return (
          <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {isPositive ? (
                <ArrowUpwardRoundedIcon sx={{ fontSize: 16, color: '#16a34a' }} />
              ) : (
                <ArrowDownwardRoundedIcon sx={{ fontSize: 16, color: '#dc2626' }} />
              )}
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  color: isPositive ? '#16a34a' : '#dc2626',
                }}
              >
                {isPositive ? `+${amt}` : `${amt}`} {Math.abs(amt) === 1 ? 'day' : 'days'}
              </Typography>
            </Box>
          </div>
        );
      },
    },
    {
      field: 'balanceAfter',
      headerName: 'Balance After',
      flex: 1.3,
      minWidth: 130,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
            {params.value != null ? `${params.value} ${params.value === 1 ? 'day' : 'days'}` : '—'}
          </Typography>
        </div>
      ),
    },
    {
      field: 'reason',
      headerName: 'Reason',
      flex: 2,
      minWidth: 180,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            label={formatReasonLabel(params.value)}
            color={getReasonColor(params.value)}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.75rem' }}
          />
        </div>
      ),
    },
    {
      field: 'actionByName',
      headerName: 'Action By',
      flex: 1.5,
      minWidth: 140,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Tooltip title={params.value || 'System'}>
            <span style={{ color: 'var(--text-secondary, #64748b)', fontSize: '0.85rem' }}>
              {params.value || 'System'}
            </span>
          </Tooltip>
        </div>
      ),
    },
  ], []);

  return (
    <div className="data-table-container">
      {/* Header */}
      <PageHeader
        title={`Leave Balance History — ${targetUserName}`}
        left={
          <IconButton onClick={() => navigate(-1)} size="medium">
            <ArrowBackIcon />
          </IconButton>
        }
      />

      {/* Snackbar */}
      {SnackbarComponent}

      {/* Data Table */}
      <div className="data-table">
        <div className="data-grid-container">
          {loading ? (
            <Box sx={{ height: '100%', overflow: 'hidden' }}>
              <TableSkeleton rows={8} columns={5} />
            </Box>
          ) : (
            <DataGrid
              rows={auditLogs}
              columns={columns}
              pagination
              paginationMode="server"
              paginationModel={paginationModel}
              rowCount={rowCount}
              pageSizeOptions={pageSizeOptions}
              onPaginationModelChange={setPaginationModel}
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              disableRowSelectionOnClick
              slots={{
                noRowsOverlay: CustomNoRowsOverlay,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaveBalanceAuditPage;
